import { isTerminalExecutionState } from "../execution-state";
import type { ExecutionHandle } from "../types";

/**
 * Monotonic handle merge. Two rules, in order:
 *
 * 1. Terminal latch — once a handle is terminal, a non-terminal frame can
 *    never regress it. The server-side transition guard runs in shadow mode,
 *    so illegal transitions CAN arrive on any channel (stream, feed, poll);
 *    this latch is load-bearing and must not be simplified away.
 * 2. Prefer the newer `updatedAt`; on ties/unparseable timestamps the incoming
 *    handle wins (matches the pre-existing behavior).
 */
export function preferLatestHandle(
  currentHandle: ExecutionHandle | null,
  nextHandle: ExecutionHandle,
): ExecutionHandle {
  if (!currentHandle) {
    return nextHandle;
  }

  if (
    isTerminalExecutionState(currentHandle.state) &&
    !isTerminalExecutionState(nextHandle.state)
  ) {
    return currentHandle;
  }

  const currentUpdatedAtMs = Date.parse(currentHandle.updatedAt);
  const nextUpdatedAtMs = Date.parse(nextHandle.updatedAt);

  if (
    Number.isFinite(currentUpdatedAtMs) &&
    Number.isFinite(nextUpdatedAtMs) &&
    currentUpdatedAtMs > nextUpdatedAtMs
  ) {
    return currentHandle;
  }

  return nextHandle;
}
